import { useCallback, useEffect, useMemo } from "react";
import { Container, Row, Col } from "reactstrap";
import BalanceTile from "./components/BalanceTile";
import EarningsTile from "./components/EarningsTile";
import {
  clearErrorMessages,
  clearSuccessMessages,
  createInstantPayout,
} from "./actions/index";
import { fetchAvailableBalance } from "../login/actions/index";

import "./index.scss";
import { connect } from "react-redux";
import { showErrorMessage, showSuccessMessage } from "../../actions";
import AccountCompleteSetup from "./AccountCompleteSetup";
import CampaignsTable from "../campaigns/components/CampaignsTable";
import LoginUserAgreementsActionRequiredBanner from "../login/components/LoginUserAgreementsActionRequiredBanner";

/**
 * @typedef {Object} OneSignal
 * @property {() => void} promptForPushNotificationsWithUserResponse
 * @property {(appId: string) => void} setAppId
 * @property {(userId: string) => void} setExternalUserId
 */

/**
 * @typedef {Object} Plugins
 * @property {OneSignal} OneSignal
 */

/** @type {Window & typeof globalThis} */
const win = window;

const Dashboard = ({
  createInstantPayout,
  fetchAvailableBalance,
  loginUser,
  errorMessage,
  clearErrorMessages,
  successMessage,
  clearSuccessMessages,
  ...props
}) => {
  useEffect(() => {
    fetchAvailableBalance();
  }, [fetchAvailableBalance]);

  useEffect(() => {
    if (errorMessage) {
      showErrorMessage(errorMessage, () => {
        clearErrorMessages();
      });
    }
  }, [errorMessage, clearErrorMessages]);

  useEffect(() => {
    if (successMessage) {
      showSuccessMessage(
        successMessage,
        () => {
          clearSuccessMessages();
        },
        "payout_success",
      );
    }
  }, [successMessage, clearSuccessMessages]);

  useEffect(() => {
    const onDeviceReady = () => {
      //Prompts the user for notification permissions.
      win.plugins.OneSignal.promptForPushNotificationsWithUserResponse();

      win.plugins.OneSignal.setAppId(process.env.REACT_APP_ONESIGNAL_APP_ID);
      win.plugins.OneSignal.setExternalUserId(loginUser._id);
    };

    document.addEventListener("deviceready", onDeviceReady, false);
    return () => document.removeEventListener("deviceready", onDeviceReady);
  }, [loginUser]);

  const handleInstantPayout = useCallback(() => {
    createInstantPayout();
  }, [createInstantPayout]);

  const payoutsDisabled = useMemo(
    () =>
      !loginUser.emailVerified
      || loginUser.needsStripeOnboarding
      || loginUser.needsStripeFinancialConnection
      // ???: agency or talent agreements?
      // ???: handle legacy creators?

      // TODO #agreements - should actually be using only userLevelRequiredIncomplete for the agreements payout block conditional but its currently broken until webhooks are fixed for bulk agreements
      || (loginUser.__agreementMetrics?.userLevelRequiredIncomplete
        && !loginUser.hasSignedCreatorAgreement),
    [loginUser],
  );

  return (
    <>
      <Container className="main-site-content dashboard-splash">
        {!payoutsDisabled ? (
          <Row>
            <Col sm="5" className="mb-4">
              <BalanceTile
                handleInstantPayout={handleInstantPayout}
                loginUser={loginUser}
                hidePayoutButton={false}
                className=""
              />
            </Col>
            <Col sm="7">
              <EarningsTile loginUser={loginUser} />
            </Col>
          </Row>
        ) : null}

        <AccountCompleteSetup />

        <LoginUserAgreementsActionRequiredBanner />
        <CampaignsTable />
      </Container>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    loginUser: state.login.loginUser,
    errorMessage: state.dashboard.errorMessage,
    successMessage: state.dashboard.successMessage,
  };
};

export default connect(mapStateToProps, {
  createInstantPayout,
  fetchAvailableBalance,
  clearErrorMessages,
  clearSuccessMessages,
  showErrorMessage,
  showSuccessMessage,
})(Dashboard);
